'use client';
import cn from 'classnames';

import Image from 'next/image';
import Text from '@/components/common/Text/Text';
import './ProductInspirationCard.style.scss';
import { ProductCardData } from '@/types/Product';
import analytics from '@/util/analytics';
import { Closet } from '@/types/Closet';
import { customerHasPremierAddOn, getCustomerTier } from '@/util/customer';
import { ParentComponent } from '@/types/Utils';

export type ProductInspirationData = ProductCardData & {
  updated_title?: string;
  sub_category?: string;
  sub_sub_category?: string;
  year?: string;
  size?: string;
  gpt_context?: string;
};

type ProductInspirationCardProps = {
  className?: string;
  imageSrc: string;
  brandOrStore: string;
  name: string;
  price: number;
  vertical?: boolean;
  customerCloset?: Closet | null;
  parentComponent: ParentComponent;

  /** Products that were recommended */
  product?: ProductInspirationData;

  /** Product(s) that generated the recommendations */
  vivrelleProduct?: ProductCardData[];
};

export const PRODUCT_CARD_IMG_HEIGHT = 130;
export const PRODUCT_CARD_IMG_WIDTH = 130;

function ProductInspirationCard(props: ProductInspirationCardProps) {
  const {
    className = '',
    imageSrc,
    brandOrStore,
    name,
    price,
    vertical,
    customerCloset,
    vivrelleProduct,
    product,
    parentComponent
  } = props;

  const handlePhotoClick = () => {
    let trackTitle = '';

    switch (parentComponent) {
      case ParentComponent.COMPLETE_THE_LOOK_GRID:
        trackTitle = 'PDP CTL Item Photo - Clicked';
        break;
      case ParentComponent.OUTFIT_INSPIRATION_APP:
        trackTitle = 'Complete the Look Checkout Item Photo - Clicked';
        break;
      case ParentComponent.CONFIRMATION_ORDER:
        trackTitle =
          'Complete the Look Order Confirmation Item Photo - Clicked';
        break;
    }

    triggerAnalytics(trackTitle);
  };

  const handleBrandAnalytics = () => {
    let trackTitle = 'Complete the Look';

    switch (parentComponent) {
      case ParentComponent.COMPLETE_THE_LOOK_GRID:
        trackTitle = 'PDP CTL Item Brand - Clicked';
        break;
      case ParentComponent.OUTFIT_INSPIRATION_APP:
        trackTitle = `${trackTitle} Checkout Item Brand - Clicked`;
        break;
      case ParentComponent.CONFIRMATION_ORDER:
        trackTitle = `${trackTitle} Order Confirmation Item Brand - Clicked`;
        break;
    }

    triggerAnalytics(trackTitle);
  };

  const handleProductNameAnalytics = () => {
    let trackTitle = 'Complete the Look';

    switch (parentComponent) {
      case ParentComponent.COMPLETE_THE_LOOK_GRID:
        trackTitle = 'PDP CTL Item Name - Clicked';
        break;
      case ParentComponent.OUTFIT_INSPIRATION_APP:
        trackTitle = `${trackTitle} Checkout Item Product Name - Clicked`;
        break;
      case ParentComponent.CONFIRMATION_ORDER:
        trackTitle = `${trackTitle} Order Confirmation Item Product Name - Clicked`;
        break;
    }

    triggerAnalytics(trackTitle);
  };

  const triggerAnalytics = (title: string) => {
    if (!vivrelleProduct || !product) return;

    analytics.track(title, {
      parent_sku: vivrelleProduct[0].sku,
      parent_sku_tier: vivrelleProduct[0].tier,
      membership_tier: customerCloset
        ? getCustomerTier(customerCloset?.subscriptionPlanProducts || [])
        : '',
      add_ons: customerCloset
        ? customerHasPremierAddOn(
            customerCloset?.subscriptionPlanProducts || []
          )
        : '',
      ...product,
      year: product.year?.toString() || ''
    });
  };

  return (
    <div
      className={cn(
        'ProductInspirationCard',
        className,
        vertical ? 'vertical' : ''
      )}
    >
      <div className={cn('ProductInspirationCard-visual-container')}>
        <Image
          className='main-img'
          src={imageSrc}
          height={PRODUCT_CARD_IMG_HEIGHT}
          width={PRODUCT_CARD_IMG_WIDTH}
          alt=''
          onClick={handlePhotoClick}
        />
      </div>

      <div className='ProductInspirationCard-title-container'>
        <div className='title-group'>
          <Text
            className={cn('title-brand')}
            variant='title-xs'
            onClick={handleBrandAnalytics}
          >
            {brandOrStore}
          </Text>
          <Text
            className={cn('title-name')}
            variant='text-s'
            onClick={handleProductNameAnalytics}
          >
            {name}
          </Text>
        </div>

        <Text
          className={cn('title-name')}
          variant='text-s'
        >
          ${price}
        </Text>
      </div>
    </div>
  );
}

export default ProductInspirationCard;
